import { Suspense, lazy } from "react";
import { RouterProvider, createHashRouter } from "react-router-dom";
import AuthenticationPage, {
  action as authAction,
} from "./pages/Authentication";
import { action as logoutAction } from "./pages/Logout";
import {
  checkAuthLoader,
  tokenLoader,
  checkAuth,
  requireCapability,
} from "./util/Auth";
import { CAPABILITIES } from "./util/Permissions";
import { filterTable, contextMenuTable } from "./components/AllDevicesPanel";
import HomePage from "./pages/Home";
import RootLayout from "./pages/Root";
import DashboardPage from "./pages/Dashboard";
import Reports from "./pages/Reports";
import ErrorComponent from "./components/ErrorComponent";
import AdminPage from "./pages/Admin";
import ModalExamplePage from "./pages/ModalExample";
import BackendPage from "./pages/BackendPage";
import ProfilePage from "./pages/Profile";
import LoaderBox from "./components/LoaderBox";

const AllDevicePanel = lazy(() => import("./components/AllDevicesPanel"));
const AllDevicesReport = lazy(() => import("./components/AllDevicesReport"));
const UserTable = lazy(() => import("./components/UserTable"));
const NewUserForm = lazy(() => import("./components/NewUserForm"));
const LoginAttempts = lazy(() => import("./components/LoginAttempts"));
const CoreApiPanel = lazy(() => import("./components/CoreApiPanel"));
const CmdApiPanel = lazy(() => import("./components/CmdApiPanel"));
const AuditLog = lazy(() => import("./components/AuditLog"));
const ActiveSessions = lazy(() => import("./components/ActiveSessions"));
const PermissionsMatrix = lazy(() =>
  import("./components/PermissionsMatrix")
);
const ServiceControl = lazy(() => import("./components/ServiceControl"));
const AlarmRecipients = lazy(() => import("./components/AlarmRecipients"));
const ModalModel = lazy(() => import("./components/ModalModel"));
const DeviceApplicationsReport = lazy(() =>
  import("./components/DeviceApplicationsReport")
);
const CalendarOfActions = lazy(() =>
  import("./components/actions/calendar/CalendarOfActions")
);
const CommunityHouse = lazy(() => import("./components/CommunityHouse"));
const MissionControl = lazy(() => import("./components/MissionControl"));
const MissionControl2 = lazy(() => import("./components/MissionControl2"));

const router = createHashRouter([
  {
    path: "/",
    element: <RootLayout />,
    id: "root",
    loader: tokenLoader,
    children: [
      {
        index: true,
        element: <HomePage />,
        errorElement: <ErrorComponent />,
        loader: checkAuthLoader,
      },
      {
        path: "auth",
        element: <AuthenticationPage />,
        errorElement: <ErrorComponent />,
        action: authAction,
        loader: checkAuth,
      },
      {
        path: "logout",
        errorElement: <ErrorComponent />,
        action: logoutAction,
      },
      {
        path: "dashboard",
        element: <DashboardPage />,
        errorElement: <ErrorComponent />,
        loader: checkAuthLoader,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <AllDevicePanel filteredBy={filterTable.all} />
              </Suspense>
            ),
          },
          {
            path: "windows",
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <AllDevicePanel filteredBy={filterTable.windows} />
              </Suspense>
            ),
          },
          {
            path: "linux",
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <AllDevicePanel filteredBy={filterTable.linux} />
              </Suspense>
            ),
          },
          {
            path: "servers",
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <AllDevicePanel filteredBy={filterTable.server} />
              </Suspense>
            ),
          },
          {
            path: "mobile",
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <AllDevicePanel filteredBy={filterTable.mobile} />
              </Suspense>
            ),
          },
          {
            path: "apple",
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <AllDevicePanel filteredBy={filterTable.apple} />
              </Suspense>
            ),
          },
          {
            path: "mission-control",
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <MissionControl />
              </Suspense>
            ),
          },
          {
            path: "mission-control-2",
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <MissionControl2 />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "reports",
        element: <Reports />,
        errorElement: <ErrorComponent />,
        loader: checkAuthLoader,
        children: [
          {
            path: "devices",
            children: [
              {
                index: true,
                path: "all",
                element: (
                  <Suspense fallback={<LoaderBox backdrop={false} />}>
                    <AllDevicesReport />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "users",
            children: [
              {
                index: true,
                path: "access",
              },
            ],
          },
          {
            path: "mobile-applications",
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <DeviceApplicationsReport />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "actions",
        element: <DashboardPage />,
        errorElement: <ErrorComponent />,
        loader: checkAuthLoader,
        children: [
          {
            path: "calendar",
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <CalendarOfActions />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "alarms",
        element: <DashboardPage />,
        errorElement: <ErrorComponent />,
        loader: checkAuthLoader,
        children: [
          {
            path: "individual",
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <AllDevicePanel
                  filteredBy={filterTable.all}
                  contextMenuTable={contextMenuTable.alarms}
                />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "community",
        element: <DashboardPage />,
        errorElement: <ErrorComponent />,
        loader: checkAuthLoader,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <CommunityHouse />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "admin",
        element: <AdminPage />,
        errorElement: <ErrorComponent />,
        loader: checkAuthLoader,
        children: [
          {
            path: "users",
            loader: requireCapability(CAPABILITIES.usersManage),
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <UserTable />
              </Suspense>
            ),
          },
          {
            path: "new-user",
            loader: requireCapability(CAPABILITIES.usersManage),
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <NewUserForm />
              </Suspense>
            ),
          },
          {
            path: "login-attempts",
            loader: requireCapability(CAPABILITIES.loginAttemptsView),
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <LoginAttempts />
              </Suspense>
            ),
          },
          {
            path: "audit",
            loader: requireCapability(CAPABILITIES.auditView),
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <AuditLog />
              </Suspense>
            ),
          },
          {
            path: "sessions",
            loader: requireCapability(CAPABILITIES.sessionsManage),
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <ActiveSessions />
              </Suspense>
            ),
          },
          {
            path: "permissions",
            loader: requireCapability(CAPABILITIES.permissionsManage),
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <PermissionsMatrix />
              </Suspense>
            ),
          },
          {
            path: "services",
            loader: requireCapability(CAPABILITIES.servicesControl),
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <ServiceControl />
              </Suspense>
            ),
          },
          {
            path: "alarm-recipients",
            loader: requireCapability(CAPABILITIES.alarmsManage),
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <AlarmRecipients />
              </Suspense>
            ),
          },
          {
            path: "backend",
            element: <BackendPage />,
            errorElement: <ErrorComponent />,
          },
          {
            path: "core-logs",
            loader: requireCapability(CAPABILITIES.servicesLogs),
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <CoreApiPanel />
              </Suspense>
            ),
          },
          {
            path: "cmd-logs",
            loader: requireCapability(CAPABILITIES.servicesLogs),
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <CmdApiPanel />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "profile",
        element: <ProfilePage />,
        errorElement: <ErrorComponent />,
        loader: checkAuthLoader,
      },
      {
        path: "modals",
        element: <ModalExamplePage />,
        errorElement: <ErrorComponent />,
        loader: checkAuthLoader,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoaderBox backdrop={false} />}>
                <ModalModel />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
]);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
